
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  RefreshCw,
  Download,
  Package,
  CheckCircle2,
  Warehouse,
  ClipboardCheck,
  ChevronLeft,
  ChevronRight,
  Filter,
  AlertCircle,
  Boxes,
} from "lucide-react";

import {
  fetchRawMaterialMaster,
  exportRawMaterialMasterExcel,
} from "./rawMaterialMasterService";

const INITIAL_FILTERS = {
  search: "",
  category: "all",
  sku_type: "all",
  is_active: "all",
  warehouse_id: "all",
  hsn_sac_code: "",
  tax_classification: "all",
  qa_required: "all",
  stock_status: "all",
};

const PAGE_SIZES = [10, 25, 50, 100];

const FIXED_COLUMNS = [
  ["material_code", "Material Code"],
  ["material_name", "Material Name"],
  ["category", "Category"],
  ["sku_type", "SKU Type"],
  ["sku_subtype", "SKU Subtype"],
  ["material_status", "Status"],
  ["standard_price", "Standard Price"],
  ["default_scrap_percent", "Scrap %"],
  ["qa_required", "QA Required"],
  ["base_uom", "Base UOM"],
  ["issue_uom", "Issue UOM"],
  ["alternate_uoms", "Alternate UOMs"],
  ["tax_classification", "Tax Classification"],
  ["hsn_sac_type", "HSN/SAC Type"],
  ["hsn_sac_code", "HSN/SAC Code"],
  ["tax_description", "Tax Description"],
  ["gst_rate", "GST %"],
  ["cgst_rate", "CGST %"],
  ["sgst_rate", "SGST %"],
  ["igst_rate", "IGST %"],
  ["warehouse_code", "Warehouse Code"],
  ["warehouse_name", "Warehouse"],
  ["on_hand_stock", "On-hand Stock"],
  ["stock_uom", "Stock UOM"],
];

const FILTER_LABELS = {
  category: "Category",
  sku_type: "SKU Type",
  is_active: "Status",
  warehouse_id: "Warehouse",
  tax_classification: "Tax Classification",
  qa_required: "QA Required",
  stock_status: "Stock Status",
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);

  return number.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const isTrue = (value) =>
  value === true || value === 1 || value === "true" || value === "1";

const isActiveMaterial = (row) =>
  isTrue(row.material_status);

const isQaRequired = (row) =>
  isTrue(row.qa_required);

const KPIIcon = ({ icon: Icon, color, background, label, value, detail }) => (
  <div className="rmm-kpi">
    <div className="rmm-kpi-top">
      <span className="rmm-kpi-label">{label}</span>
      <span className="rmm-kpi-icon" style={{ color, background }}>
        <Icon size={20} />
      </span>
    </div>
    <div className="rmm-kpi-value">{value}</div>
    <div className="rmm-kpi-detail">{detail}</div>
  </div>
);

export default function RawMaterialMasterPage() {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [report, setReport] = useState({
    columns: [],
    rows: [],
    total_rows: 0,
  });

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showFilters, setShowFilters] = useState(true);
  const [visibleColumns, setVisibleColumns] = useState(null);

  const loadReport = useCallback(async (activeFilters) => {
    setLoading(true);
    setError("");

    try {
      const params = { ...activeFilters };

      Object.keys(params).forEach((key) => {
        if (params[key] === "all" || params[key] === "") {
          delete params[key];
        }
      });

      const data = await fetchRawMaterialMaster(params);

      setReport({
        columns: Array.isArray(data.columns) ? data.columns : [],
        rows: Array.isArray(data.rows) ? data.rows : [],
        total_rows: Number(data.total_rows || 0),
      });

      setVisibleColumns(null);
      setPage(1);
    } catch (err) {
      console.error("Raw Material Master report failed:", err);

      setReport({ columns: [], rows: [], total_rows: 0 });
      setError(
        err.response?.data?.message ||
          err.message ||
          "Unable to load the Raw Material Master report."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport(INITIAL_FILTERS);
  }, [loadReport]);

  const allColumns = useMemo(() => {
    const keys = new Set(report.columns);
    const result = [];

    FIXED_COLUMNS.forEach(([key, label]) => {
      if (keys.has(key)) result.push({ key, label });
    });

    const fixedKeys = new Set(FIXED_COLUMNS.map(([key]) => key));

    report.columns.forEach((key) => {
      if (!fixedKeys.has(key) && key !== "material_id") {
        result.push({ key, label: key.replaceAll("_", " ") });
      }
    });

    return result;
  }, [report.columns]);

  const columns = useMemo(() => {
    if (!visibleColumns) return allColumns;

    return allColumns.filter((column) =>
      visibleColumns.includes(column.key)
    );
  }, [allColumns, visibleColumns]);

  const rows = report.rows;

  const kpis = useMemo(() => {
    const uniqueMaterials = new Map();

    rows.forEach((row) => {
      uniqueMaterials.set(row.material_id, row);
    });

    const materials = Array.from(uniqueMaterials.values());

    return {
      totalMaterials: materials.length,
      activeMaterials: materials.filter(isActiveMaterial).length,
      warehouseRecords: rows.length,
      qaRequired: materials.filter(isQaRequired).length,
    };
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const pageRows = rows.slice(startIndex, startIndex + pageSize);

  const updateFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    loadReport(filters);
  };

  const resetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    loadReport(INITIAL_FILTERS);
  };

  const handleExport = async () => {
    setExporting(true);
    setError("");

    try {
      const blob = await exportRawMaterialMasterExcel(appliedFilters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = "raw_material_master.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Raw Material Master export failed:", err);
      setError(
        err.response?.data?.message ||
          "Excel export failed. Please try again."
      );
    } finally {
      setExporting(false);
    }
  };

  const toggleColumn = (key) => {
    setVisibleColumns((current) => {
      const active = current || allColumns.map((column) => column.key);

      return active.includes(key)
        ? active.filter((item) => item !== key)
        : [...active, key];
    });
  };

  const renderCell = (row, key) => {
    const value = row[key];

    if (key === "material_status") {
      const active = isTrue(value);

      return (
        <span className={`rmm-status ${active ? "active" : "inactive"}`}>
          <span className="rmm-status-dot" />
          {active ? "Active" : "Inactive"}
        </span>
      );
    }

    if (key === "qa_required") {
      return (
        <span className={`rmm-pill ${isTrue(value) ? "yes" : "no"}`}>
          {isTrue(value) ? "Required" : "Not required"}
        </span>
      );
    }

    if (["standard_price"].includes(key)) {
      return formatAmount(value);
    }

    if (
      ["gst_rate", "cgst_rate", "sgst_rate", "igst_rate",
       "default_scrap_percent"].includes(key)
    ) {
      return value === null || value === undefined || value === ""
        ? "—"
        : `${formatAmount(value)}%`;
    }

    if (key === "on_hand_stock") {
      return (
        <span className="rmm-stock-number">
          {formatAmount(value)}
        </span>
      );
    }

    return formatValue(value);
  };

  return (
    <div className="rmm-page">
      <style>{`
        .rmm-page {
          --rmm-ink: #172033;
          --rmm-muted: #748096;
          --rmm-line: #e6eaf1;
          --rmm-primary: #4f46e5;
          color: var(--rmm-ink);
          padding: 24px;
          background: #f6f8fc;
          min-height: 100%;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }
        .rmm-page * { box-sizing: border-box; }
        .rmm-header {
          display: flex; align-items: center; justify-content: space-between;
          gap: 18px; flex-wrap: wrap; margin-bottom: 24px;
        }
        .rmm-eyebrow {
          color: #5b5bd6; font-size: 11px; font-weight: 800;
          letter-spacing: 1.3px; text-transform: uppercase; margin-bottom: 7px;
        }
        .rmm-title { font-size: 27px; font-weight: 800; letter-spacing: -.7px; margin: 0; }
        .rmm-subtitle { color: var(--rmm-muted); font-size: 13px; margin-top: 7px; }
        .rmm-header-actions { display: flex; gap: 9px; flex-wrap: wrap; }
        .rmm-btn {
          border: 1px solid var(--rmm-line); border-radius: 10px; padding: 10px 14px;
          background: white; color: #344054; display: inline-flex; gap: 8px;
          align-items: center; justify-content: center; cursor: pointer;
          font-size: 12px; font-weight: 700; transition: .15s ease;
        }
        .rmm-btn:hover { border-color: #b9c0d2; transform: translateY(-1px); }
        .rmm-btn:disabled { opacity: .55; cursor: not-allowed; transform: none; }
        .rmm-btn-primary { background: var(--rmm-primary); border-color: var(--rmm-primary); color: white; }
        .rmm-btn-export { color: #087f5b; border-color: #b9e8d5; background: #f1fcf7; }
        .rmm-kpis {
          display: grid; grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 15px; margin-bottom: 20px;
        }
        .rmm-kpi {
          background: white; border: 1px solid var(--rmm-line); border-radius: 15px;
          padding: 19px; box-shadow: 0 2px 8px rgba(25, 40, 75, .025);
        }
        .rmm-kpi-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
        .rmm-kpi-label { color: #69758b; font-size: 12px; font-weight: 650; }
        .rmm-kpi-icon { width: 39px; height: 39px; display: grid; place-items: center; border-radius: 12px; }
        .rmm-kpi-value { font-size: 29px; font-weight: 800; letter-spacing: -.8px; margin-top: 13px; }
        .rmm-kpi-detail { color: #8b95a7; font-size: 11px; margin-top: 4px; }
        .rmm-panel { background: white; border: 1px solid var(--rmm-line); border-radius: 15px; margin-bottom: 18px; overflow: hidden; }
        .rmm-panel-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 17px 19px; border-bottom: 1px solid #edf0f5; }
        .rmm-panel-title { display: flex; align-items: center; gap: 9px; font-weight: 800; font-size: 14px; }
        .rmm-panel-caption { color: var(--rmm-muted); font-size: 11px; margin-top: 4px; }
        .rmm-filters { padding: 18px; }
        .rmm-filter-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; }
        .rmm-field { min-width: 0; display: flex; flex-direction: column; gap: 7px; }
        .rmm-field label { font-size: 11px; font-weight: 750; color: #515d72; }
        .rmm-control {
          width: 100%; min-width: 0; height: 39px; border: 1px solid #dfe4ed;
          border-radius: 9px; padding: 0 11px; background: #fff; color: #344054;
          font-size: 12px; outline: none;
        }
        .rmm-control:focus { border-color: #818cf8; box-shadow: 0 0 0 3px rgba(99, 102, 241, .10); }
        .rmm-search-wrap { position: relative; }
        .rmm-search-wrap svg { position: absolute; left: 11px; top: 12px; color: #8b95a7; }
        .rmm-search-wrap .rmm-control { padding-left: 35px; }
        .rmm-filter-actions { display: flex; justify-content: flex-end; gap: 9px; margin-top: 17px; flex-wrap: wrap; }
        .rmm-error { display: flex; align-items: flex-start; gap: 10px; border: 1px solid #fecaca; background: #fff5f5; color: #b42318; padding: 13px 15px; border-radius: 10px; margin-bottom: 16px; font-size: 12px; }
        .rmm-table-wrap { width: 100%; overflow: auto; }
        .rmm-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12px; white-space: nowrap; }
        .rmm-table th { background: #f8f9fc; color: #68758a; text-align: left; font-size: 10px; letter-spacing: .45px; text-transform: uppercase; padding: 13px 15px; border-bottom: 1px solid var(--rmm-line); font-weight: 800; }
        .rmm-table td { padding: 14px 15px; border-bottom: 1px solid #eff1f5; color: #3d485c; max-width: 260px; overflow: hidden; text-overflow: ellipsis; }
        .rmm-table tbody tr:hover td { background: #fafbff; }
        .rmm-table tbody tr:last-child td { border-bottom: 0; }
        .rmm-code { font-weight: 800; color: #4f46e5 !important; }
        .rmm-name { font-weight: 750; color: #202b40 !important; }
        .rmm-status { display: inline-flex; align-items: center; gap: 6px; border-radius: 20px; padding: 5px 8px; font-size: 10px; font-weight: 800; }
        .rmm-status-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
        .rmm-status.active { background: #eafaf2; color: #13845a; }
        .rmm-status.inactive { background: #f1f3f7; color: #707b8e; }
        .rmm-pill { display: inline-block; border-radius: 6px; padding: 5px 7px; font-size: 10px; font-weight: 750; }
        .rmm-pill.yes { background: #fff4e5; color: #a35a00; }
        .rmm-pill.no { background: #f0f3f8; color: #667085; }
        .rmm-stock-number { font-weight: 800; font-variant-numeric: tabular-nums; color: #253653; }
        .rmm-empty { text-align: center; padding: 45px 20px !important; white-space: normal; }
        .rmm-empty-icon { display: inline-grid; place-items: center; width: 50px; height: 50px; background: #f1f3ff; color: #6264db; border-radius: 15px; margin-bottom: 12px; }
        .rmm-empty-title { font-size: 14px; font-weight: 800; color: #344054; }
        .rmm-empty-text { color: #8791a3; font-size: 12px; margin-top: 6px; }
        .rmm-pagination { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; padding: 15px 18px; border-top: 1px solid #edf0f5; }
        .rmm-page-info { color: #748096; font-size: 11px; }
        .rmm-page-controls { display: flex; align-items: center; gap: 6px; }
        .rmm-page-btn { width: 32px; height: 32px; display: grid; place-items: center; border: 1px solid #e1e5ed; border-radius: 8px; background: white; cursor: pointer; color: #526078; }
        .rmm-page-btn:disabled { opacity: .4; cursor: not-allowed; }
        .rmm-page-number { font-size: 11px; font-weight: 750; padding: 0 7px; }
        .rmm-size-select { height: 32px; border: 1px solid #e1e5ed; border-radius: 8px; padding: 0 8px; font-size: 11px; background: white; }
        .rmm-column-menu { display: flex; gap: 7px; flex-wrap: wrap; padding: 12px 18px; border-bottom: 1px solid #edf0f5; }
        .rmm-column-chip { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #e1e5ed; background: white; border-radius: 20px; padding: 6px 9px; color: #536077; font-size: 10px; cursor: pointer; }
        .rmm-column-chip.selected { background: #eef2ff; border-color: #c7d2fe; color: #4338ca; }
        .rmm-spinner { width: 15px; height: 15px; border: 2px solid #d9def5; border-top-color: #4f46e5; border-radius: 50%; display: inline-block; animation: rmm-spin .7s linear infinite; }
        @keyframes rmm-spin { to { transform: rotate(360deg); } }
        @media (max-width: 1000px) { .rmm-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); } .rmm-filter-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 600px) { .rmm-page { padding: 14px; } .rmm-title { font-size: 23px; } .rmm-kpis { gap: 9px; } .rmm-kpi { padding: 13px; } .rmm-kpi-value { font-size: 24px; } .rmm-filter-grid { grid-template-columns: 1fr; } .rmm-header-actions { width: 100%; } .rmm-header-actions .rmm-btn { flex: 1; } }
      `}</style>

      <div className="rmm-header">
        <div>
          <div className="rmm-eyebrow">Inventory Intelligence</div>
          <h1 className="rmm-title">Raw Material Master</h1>
          <div className="rmm-subtitle">
            Material attributes, tax configuration and warehouse stock overview
          </div>
        </div>

        <div className="rmm-header-actions">
          <button
            className="rmm-btn"
            onClick={() => loadReport(appliedFilters)}
            disabled={loading}
          >
            <RefreshCw size={15} />
            Refresh
          </button>

          <button
            className="rmm-btn rmm-btn-export"
            onClick={handleExport}
            disabled={exporting || loading}
          >
            {exporting ? <span className="rmm-spinner" /> : <Download size={15} />}
            {exporting ? "Exporting..." : "Export Excel"}
          </button>
        </div>
      </div>

      {error && (
        <div className="rmm-error">
          <AlertCircle size={17} />
          <div>{error}</div>
        </div>
      )}

      <div className="rmm-kpis">
        <KPIIcon
          icon={Package}
          color="#4f46e5"
          background="#eef0ff"
          label="Total Materials"
          value={loading ? "—" : kpis.totalMaterials.toLocaleString("en-IN")}
          detail="Unique materials in current results"
        />
        <KPIIcon
          icon={CheckCircle2}
          color="#0f9467"
          background="#e9fbf3"
          label="Active Materials"
          value={loading ? "—" : kpis.activeMaterials.toLocaleString("en-IN")}
          detail="Active material master records"
        />
        <KPIIcon
          icon={Warehouse}
          color="#bd7c17"
          background="#fff5df"
          label="Warehouse Records"
          value={loading ? "—" : report.total_rows.toLocaleString("en-IN")}
          detail="Material–warehouse rows returned"
        />
        <KPIIcon
          icon={ClipboardCheck}
          color="#8a51cc"
          background="#f5edff"
          label="QA Required"
          value={loading ? "—" : kpis.qaRequired.toLocaleString("en-IN")}
          detail="Unique materials requiring QA"
        />
      </div>

      <section className="rmm-panel">
        <div className="rmm-panel-heading">
          <div>
            <div className="rmm-panel-title">
              <Filter size={17} color="#5b5bd6" />
              Search & Filters
            </div>
            <div className="rmm-panel-caption">
              Narrow the report using material, warehouse and compliance details
            </div>
          </div>

          <button
            className="rmm-btn"
            onClick={() => setShowFilters((current) => !current)}
          >
            {showFilters ? "Hide filters" : "Show filters"}
          </button>
        </div>

        {showFilters && (
          <div className="rmm-filters">
            <div className="rmm-field" style={{ marginBottom: 15 }}>
              <label>Global Search</label>
              <div className="rmm-search-wrap">
                <Search size={15} />
                <input
                  className="rmm-control"
                  placeholder="Search material code, name, category, HSN/SAC or warehouse..."
                  value={filters.search}
                  onChange={(event) => updateFilter("search", event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") applyFilters();
                  }}
                />
              </div>
            </div>

            <div className="rmm-filter-grid">
              <div className="rmm-field">
                <label>Category</label>
                <input
                  className="rmm-control"
                  placeholder="Enter category"
                  value={filters.category === "all" ? "" : filters.category}
                  onChange={(event) =>
                    updateFilter("category", event.target.value || "all")
                  }
                />
              </div>

              <div className="rmm-field">
                <label>SKU Type</label>
                <input
                  className="rmm-control"
                  placeholder="Enter SKU type"
                  value={filters.sku_type === "all" ? "" : filters.sku_type}
                  onChange={(event) =>
                    updateFilter("sku_type", event.target.value || "all")
                  }
                />
              </div>

              <div className="rmm-field">
                <label>Active Status</label>
                <select
                  className="rmm-control"
                  value={filters.is_active}
                  onChange={(event) => updateFilter("is_active", event.target.value)}
                >
                  <option value="all">All statuses</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="rmm-field">
                <label>Warehouse ID</label>
                <input
                  className="rmm-control"
                  type="number"
                  min="1"
                  placeholder="Enter warehouse ID"
                  value={filters.warehouse_id === "all" ? "" : filters.warehouse_id}
                  onChange={(event) =>
                    updateFilter("warehouse_id", event.target.value || "all")
                  }
                />
              </div>

              <div className="rmm-field">
                <label>HSN / SAC Code</label>
                <input
                  className="rmm-control"
                  placeholder="Enter HSN/SAC code"
                  value={filters.hsn_sac_code}
                  onChange={(event) => updateFilter("hsn_sac_code", event.target.value)}
                />
              </div>

              <div className="rmm-field">
                <label>Tax Classification</label>
                <input
                  className="rmm-control"
                  placeholder="Enter classification"
                  value={
                    filters.tax_classification === "all"
                      ? ""
                      : filters.tax_classification
                  }
                  onChange={(event) =>
                    updateFilter("tax_classification", event.target.value || "all")
                  }
                />
              </div>

              <div className="rmm-field">
                <label>QA Required</label>
                <select
                  className="rmm-control"
                  value={filters.qa_required}
                  onChange={(event) => updateFilter("qa_required", event.target.value)}
                >
                  <option value="all">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>

              <div className="rmm-field">
                <label>Stock Status</label>
                <select
                  className="rmm-control"
                  value={filters.stock_status}
                  onChange={(event) => updateFilter("stock_status", event.target.value)}
                >
                  <option value="all">All stock</option>
                  <option value="ZERO">Zero stock</option>
                  <option value="AVAILABLE">Positive on-hand stock</option>
                </select>
              </div>
            </div>

            <div className="rmm-filter-actions">
              <button className="rmm-btn" onClick={resetFilters}>
                Reset
              </button>
              <button
                className="rmm-btn rmm-btn-primary"
                onClick={applyFilters}
                disabled={loading}
              >
                <Search size={14} />
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="rmm-panel">
        <div className="rmm-panel-heading">
          <div>
            <div className="rmm-panel-title">
              <Boxes size={17} color="#5b5bd6" />
              Material & Warehouse Details
            </div>
            <div className="rmm-panel-caption">
              {report.total_rows.toLocaleString("en-IN")} rows · Dynamic attribute columns included
            </div>
          </div>

          <button
            className="rmm-btn"
            onClick={() => setVisibleColumns(null)}
            title="Show all columns"
          >
            Reset Columns
          </button>
        </div>

        {allColumns.length > 0 && (
          <div className="rmm-column-menu">
            {allColumns.map((column) => {
              const active = columns.some((item) => item.key === column.key);

              return (
                <button
                  type="button"
                  key={column.key}
                  className={`rmm-column-chip ${active ? "selected" : ""}`}
                  onClick={() => toggleColumn(column.key)}
                >
                  <span>{active ? "✓" : "+"}</span>
                  {column.label}
                </button>
              );
            })}
          </div>
        )}

        <div className="rmm-table-wrap">
          <table className="rmm-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td className="rmm-empty" colSpan={Math.max(columns.length, 1)}>
                    <span className="rmm-spinner" />
                    <div className="rmm-empty-title" style={{ marginTop: 12 }}>
                      Loading report...
                    </div>
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td className="rmm-empty" colSpan={Math.max(columns.length, 1)}>
                    <div className="rmm-empty-icon">
                      <Package size={24} />
                    </div>
                    <div className="rmm-empty-title">No materials found</div>
                    <div className="rmm-empty-text">
                      Try adjusting the search terms or filters.
                    </div>
                  </td>
                </tr>
              ) : (
                pageRows.map((row, index) => (
                  <tr key={`${row.material_id}-${row.warehouse_id ?? "none"}-${index}`}>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={
                          column.key === "material_code"
                            ? "rmm-code"
                            : column.key === "material_name"
                              ? "rmm-name"
                              : ""
                        }
                        title={formatValue(row[column.key])}
                      >
                        {renderCell(row, column.key)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="rmm-pagination">
          <div className="rmm-page-info">
            {rows.length === 0
              ? "Showing 0 records"
              : `Showing ${startIndex + 1}–${Math.min(startIndex + pageSize, rows.length)} of ${rows.length} rows`}
          </div>

          <div className="rmm-page-controls">
            <span className="rmm-page-info">Rows</span>
            <select
              className="rmm-size-select"
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>

            <button
              className="rmm-page-btn"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={currentPage <= 1 || loading}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>

            <span className="rmm-page-number">
              {currentPage} / {totalPages}
            </span>

            <button
              className="rmm-page-btn"
              onClick={() =>
                setPage((current) => Math.min(totalPages, current + 1))
              }
              disabled={currentPage >= totalPages || loading}
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
